import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { loadSlobozip, type SlobozipApi } from './wasm';

type WasmState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; api: SlobozipApi }
  | { status: 'error'; error: Error };

const WasmContext = createContext<WasmState | null>(null);

/**
 * WasmProvider — kicks off the WASM module load on mount and exposes
 * the typed API + loading status to descendants. One module per page.
 */
export function WasmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<WasmState>({ status: 'idle' });

  useEffect(() => {
    let alive = true;
    setState({ status: 'loading' });

    loadSlobozip()
      .then((api) => {
        if (!alive) return;
        setState({ status: 'ready', api });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setState({ status: 'error', error });
      });

    return () => {
      alive = false;
    };
  }, []);

  return <WasmContext.Provider value={state}>{children}</WasmContext.Provider>;
}

/**
 * useSlobozip — read the current WASM state. Components should switch
 * on `state.status` to render loading / error / ready states.
 */
export function useSlobozip(): WasmState {
  const ctx = useContext(WasmContext);
  if (!ctx) throw new Error('useSlobozip must be used inside <WasmProvider>');
  return ctx;
}
